import { Tabs } from '@design-systems/canvas-ui'
import { useMemo } from 'react'
import { MPMLayout } from 'components/layout'
import { PageTabs } from 'components/page-tabs'
import { UrlParams } from 'enums/url-params'
import { clearLocationParam, isEntitled } from 'utils/helpers'

import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from 'components/error-fallback'
import { useEssentials } from 'hooks/utils/useEssentials'
import { Constants } from 'constants/constants'
import { useLogClick } from 'utils/analytics'

import { isStore } from 'utils/location'

import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { BackButton } from 'components/button/back-button'

const ROUTES = {
    OVERVIEW: 'overview',
    LOCATION: 'location',
    DETAILS: 'details',
    HOURLY: 'hourly',
}

export const SalesPage = () => {
    const { params, setParams } = useEssentials()
    window.onpopstate = (e) => {
        clearLocationParam({ params, setParams })
    }

    return <Sales />
}

export const Sales = () => {
    const { sales } = TRANSLATION_KEYS
    const { tabs: tabsKeys, overview } = sales
    const navigate = useNavigate()
    const locationFromRouter = useLocation()
    const { t, params, greenfieldGroups, location } = useEssentials()
    const currentLocation =
        params.get(UrlParams.LOCATION_ID) ?? location.shortName
    const { SALES_LOCATION, ADMIN_PROD } = Constants.GREENFIELD_GROUPS
    const isAuth = isEntitled(greenfieldGroups ?? [], [
        SALES_LOCATION,
        ADMIN_PROD,
    ])

    // we need differentiate tabs (tab.key) and tab translations (tab.label) to ensure
    // our routing stays in english, but our display can switch from english to spanish

    const tabsWithTranslations = useMemo(() => {
        const tabs = [
            { key: ROUTES.OVERVIEW, label: t(tabsKeys.overview) },
            { key: ROUTES.DETAILS, label: t(tabsKeys.details) },
        ]
        if (!isStore(currentLocation) && isAuth) {
            tabs.push({
                key: ROUTES.LOCATION,
                label: t(tabsKeys.location),
            })
        }
        return tabs
    }, [currentLocation, isAuth, t, tabsKeys])

    const { logClick } = useLogClick('sales')

    const currentPath = locationFromRouter.pathname.split('/').pop() || ''

    const isHourlySales = currentPath === ROUTES.HOURLY
    const activeTab = isHourlySales
        ? ROUTES.OVERVIEW
        : (currentPath ?? ROUTES.OVERVIEW)

    const handleTabSelect = (activeTab: string) => {
        const path = activeTab
        logClick('sales tab ' + activeTab)
        void navigate(`/sales/${path}${locationFromRouter.search}`)
    }
    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(sales.title)}
                />
            )}
        >
            <MPMLayout
                heading={
                    isHourlySales ? t(overview.hourlySales) : t(sales.title)
                }
                className="hc8-ph-none sales-page"
                isMenuOverride={isHourlySales}
                menuOverride={<BackButton />}
                hasTabs
                tabs={
                    <PageTabs
                        activeTab={activeTab}
                        className="tabs hc8-pv-2x"
                        disableAdaptive={true}
                        onTabSelect={handleTabSelect}
                    >
                        {tabsWithTranslations.map((tab) => (
                            <Tabs.Item key={tab.key} name={tab.key}>
                                {tab.label}
                            </Tabs.Item>
                        ))}
                    </PageTabs>
                }
            >
                <Outlet />
            </MPMLayout>
        </ErrorBoundary>
    )
}
